import { useState } from 'react';
import useScrollAnimation from '../../../hooks/useScrollAnimation';

const faqs = [
  {
    q: '¿Necesito saber de computación avanzada?',
    a: 'No. Si sabes usar WhatsApp, sabes usar StockSimple. Es 100% intuitivo. No necesitas cursos, tutoriales ni conocimientos técnicos. Lo abres y empiezas a usar.',
    highlight: '100% intuitivo',
  },
  {
    q: '¿Es un pago mensual?',
    a: 'Negativo. Es un pago único de S/20. Sin letras pequeñas, sin cobros recurrentes, sin sorpresas. Lo pagas una vez y es tuyo para siempre.',
    highlight: 'pago único de S/20',
  },
  {
    q: '¿Funciona para cualquier tipo de tienda?',
    a: 'Ideal para ferreterías, boutiques, bodegas y cualquier negocio que maneje productos físicos. Si tienes un inventario, StockSimple es para ti.',
    highlight: 'Ferreterías, boutiques, bodegas',
  },
  {
    q: '¿Qué pasa si cambio de equipo?',
    a: 'Tus datos están seguros y puedes acceder desde donde necesites. Cambia de celular, tablet o computadora sin perder nada. Todo sincronizado.',
    highlight: 'Acceso desde cualquier dispositivo',
  },
  {
    q: '¿Cuánto tiempo toma configurarlo?',
    a: 'Menos de 5 minutos. Diseñado para que cualquier persona pueda empezar inmediatamente. Subes tus productos, defines cantidades y listo. Tu bodega digital ya está funcionando.',
    highlight: 'Menos de 5 minutos',
  },
];

const FAQItem = ({ q, a, highlight }: { q: string; a: string; highlight: string }) => {
  const [open, setOpen] = useState(false);

  return (
    <div
      className={`border border-gray-100 rounded-2xl overflow-hidden transition-all ${open ? 'bg-emerald-50/40' : 'bg-white hover:bg-gray-50/60'}`}
    >
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-4 sm:px-6 py-4 sm:py-5 text-left cursor-pointer gap-3"
      >
        <span className="text-sm sm:text-base font-semibold text-gray-900">{q}</span>
        <span className={`w-8 h-8 flex items-center justify-center rounded-full flex-shrink-0 transition-all ${open ? 'bg-emerald-500 text-white' : 'bg-gray-100 text-gray-500'}`}>
          <i className={`${open ? 'ri-subtract-line' : 'ri-add-line'} text-lg`} />
        </span>
      </button>
      {open && (
        <div className="px-4 sm:px-6 pb-4 sm:pb-5 slide-down">
          <p className="text-gray-600 text-sm leading-relaxed">
            {a.split(highlight).map((part, i) =>
              i === 0 ? (
                <span key={i}>{part}</span>
              ) : (
                <>
                  <strong key={`h-${i}`} className="text-emerald-600 font-semibold">{highlight}</strong>
                  <span key={`p-${i}`}>{part}</span>
                </>
              )
            )}
          </p>
        </div>
      )}
    </div>
  );
};

const FAQSection = () => {
  const { ref, visible } = useScrollAnimation();

  return (
    <section id="faq" className="py-16 sm:py-24 bg-gray-50/60">
      <div className="max-w-3xl mx-auto px-4 sm:px-6">
        <div ref={ref} className={`text-center mb-10 sm:mb-12 ${visible ? 'anim-fade-up' : 'scroll-hidden'}`}>
          <span className="inline-block bg-gray-100 text-gray-600 text-xs font-bold uppercase tracking-widest px-4 py-1.5 rounded-full mb-4">
            Preguntas Frecuentes
          </span>
          <h2 className="text-2xl sm:text-3xl lg:text-5xl font-black text-gray-900 leading-tight" style={{ fontFamily: "'Syne', sans-serif" }}>
            Tenemos las <span className="text-emerald-600">respuestas.</span>
          </h2>
          <p className="text-gray-500 mt-3 text-sm sm:text-base">Todo lo que necesitas saber antes de dar el paso.</p>
        </div>

        <div className={`flex flex-col gap-3 ${visible ? 'anim-fade-up-1' : 'scroll-hidden'}`}>
          {faqs.map((f) => (
            <FAQItem key={f.q} {...f} />
          ))}
        </div>

        <div className={`mt-8 sm:mt-10 bg-white border border-emerald-100 rounded-2xl p-5 sm:p-6 flex items-start sm:items-center gap-3 sm:gap-4 ${visible ? 'anim-fade-up-2' : 'scroll-hidden'}`}>
          <div className="w-11 h-11 flex items-center justify-center bg-emerald-100 rounded-xl flex-shrink-0">
            <i className="ri-customer-service-2-line text-emerald-600 text-xl" />
          </div>
          <div>
            <p className="text-gray-800 font-semibold text-sm">¿Tienes otra pregunta?</p>
            <p className="text-gray-500 text-xs sm:text-sm">Escríbenos y te respondemos en menos de 24 horas. Somos personas reales.</p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default FAQSection;
